import React, { useMemo, useState } from 'react';
import BopRowActions, { RowIcon } from '../../../../components/BopRowActions';
import { BADGE, BADGE_SCONOSCIUTI, DIPENDENTI, OGGI, Badge, formatData, formatDataOra } from './dati';

type FiltroStato = '' | 'attivi' | 'non_attivi';

interface FormBadge {
  id: number | null;
  dipendenteId: string;
  codice: string;
  descrizione: string;
  validoDal: string;
  validoAl: string;
  attivo: boolean;
}

const FORM_VUOTO: FormBadge = {
  id: null, dipendenteId: '', codice: '', descrizione: '', validoDal: OGGI, validoAl: '', attivo: true,
};

function statoBadge(b: Badge): { label: string; badge: string; attivo: boolean } {
  if (!b.attivo) return { label: 'Disattivato', badge: 'bop-badge--neutral', attivo: false };
  if (b.validoAl && b.validoAl < OGGI) return { label: 'Scaduto', badge: 'bop-badge--warning', attivo: false };
  if (b.validoDal > OGGI) return { label: 'Non ancora valido', badge: 'bop-badge--info', attivo: false };
  return { label: 'Attivo', badge: 'bop-badge--success', attivo: true };
}

export default function VistaBadge() {
  const [badge, setBadge] = useState<Badge[]>(BADGE);
  const [sconosciuti, setSconosciuti] = useState(BADGE_SCONOSCIUTI);
  const [fStato, setFStato] = useState<FiltroStato>('');
  const [fQ, setFQ] = useState('');
  const [form, setForm] = useState<FormBadge | null>(null);
  const [errore, setErrore] = useState('');

  const visibili = useMemo(() => {
    const q = fQ.trim().toLowerCase();
    return badge.filter((b) => {
      const attivo = statoBadge(b).attivo;
      if (fStato === 'attivi' && !attivo) return false;
      if (fStato === 'non_attivi' && attivo) return false;
      const testo = `${b.codice} ${b.dipendente.cognome} ${b.dipendente.nome} ${b.descrizione ?? ''}`.toLowerCase();
      return !q || testo.includes(q);
    });
  }, [badge, fStato, fQ]);

  const apriNuovo = (codice = '') => {
    setErrore('');
    setForm({ ...FORM_VUOTO, codice });
  };

  const apriModifica = (b: Badge) => {
    setErrore('');
    setForm({
      id: b.id, dipendenteId: String(b.dipendente.id), codice: b.codice, descrizione: b.descrizione ?? '',
      validoDal: b.validoDal, validoAl: b.validoAl ?? '', attivo: b.attivo,
    });
  };

  const salva = () => {
    if (!form) return;
    const codice = form.codice.trim().toUpperCase();
    const dipendente = DIPENDENTI.find((d) => d.id === Number(form.dipendenteId));
    if (!dipendente) { setErrore('Scegli il dipendente'); return; }
    if (!/^[A-Z0-9]{4,20}$/.test(codice)) { setErrore('Codice badge: da 4 a 20 caratteri, solo lettere e cifre'); return; }
    if (badge.some((b) => b.codice === codice && b.id !== form.id)) {
      setErrore(`Il badge ${codice} è già registrato`);
      return;
    }
    if (!form.validoDal) { setErrore("Indica da quando vale il badge"); return; }
    if (form.validoAl && form.validoAl < form.validoDal) {
      setErrore("La fine della validità è precedente all'inizio");
      return;
    }
    const record: Badge = {
      id: form.id ?? Date.now(), codice, descrizione: form.descrizione.trim() || null,
      dipendente: { id: dipendente.id, nome: dipendente.nome, cognome: dipendente.cognome },
      validoDal: form.validoDal, validoAl: form.validoAl || null, attivo: form.attivo, ultimoUtilizzoAt: null,
    };
    setBadge(form.id === null
      ? [...badge, record]
      : badge.map((b) => (b.id === form.id ? { ...record, ultimoUtilizzoAt: b.ultimoUtilizzoAt } : b)));
    setSconosciuti(sconosciuti.filter((s) => s.codiceBadge !== codice));
    setForm(null);
  };

  const disattiva = (b: Badge) => {
    setBadge(badge.map((x) => (x.id === b.id ? { ...x, attivo: false } : x)));
  };

  return (
    <div className="bop-ricarica__stack">
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2 className="bop-page-title">Badge</h2>
          <p className="bop-page-subtitle">Badge di ricarica dei dipendenti: valgono su tutte le colonnine del gruppo</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={() => apriNuovo()}>+ Nuovo badge</button>
      </div>

      {sconosciuti.length > 0 && (
        <div className="bop-card">
          <div className="bop-card__header"><h3 className="bop-card__title">Badge sconosciuti</h3></div>
          <div className="bop-card__body">
            <p className="bop-ricarica__nota">
              Tentativi rifiutati negli ultimi 30 giorni sulle colonnine della società. Per registrare un badge nuovo
              avvicinalo a una colonnina, poi clicca Abilita.
            </p>
          </div>
          <table className="bop-table">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Colonnina</th>
                <th className="bop-text-right">Tentativi</th>
                <th>Ultimo tentativo</th>
                <th className="bop-table__th--actions">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {sconosciuti.map((s) => (
                <tr key={s.codiceBadge}>
                  <td className="bop-text-mono">{s.codiceBadge}</td>
                  <td>
                    {s.colonnina.nome}
                    <div className="bop-ricarica__secondaria">{s.colonnina.sede ?? ''}</div>
                  </td>
                  <td className="bop-text-right">{s.tentativi}</td>
                  <td>{formatDataOra(s.ultimoTentativoAt)}</td>
                  <td className="bop-table__td--actions">
                    <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => apriNuovo(s.codiceBadge)}>
                      Abilita
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bop-filter-bar">
        <select className="bop-select" value={fStato} onChange={(e) => setFStato(e.target.value as FiltroStato)}>
          <option value="">Tutti i badge</option>
          <option value="attivi">Solo attivi</option>
          <option value="non_attivi">Non attivi</option>
        </select>
        <input className="bop-input" placeholder="Cerca codice, dipendente o descrizione…" value={fQ}
          onChange={(e) => setFQ(e.target.value)} />
      </div>

      <div className="bop-card">
        <div className="bop-table-wrapper">
          {visibili.length === 0 ? (
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Nessun badge</h3>
              <p className="bop-empty-state__text">Nessun badge trovato con i filtri selezionati</p>
            </div>
          ) : (
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Codice</th>
                  <th>Dipendente</th>
                  <th>Descrizione</th>
                  <th>Validità</th>
                  <th>Ultimo utilizzo</th>
                  <th>Stato</th>
                  <th className="bop-table__th--actions">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {visibili.map((b) => {
                  const s = statoBadge(b);
                  return (
                    <tr key={b.id} onClick={() => apriModifica(b)}>
                      <td className="bop-text-mono">{b.codice}</td>
                      <td>{b.dipendente.cognome} {b.dipendente.nome}</td>
                      <td className="bop-text-muted">{b.descrizione ?? '—'}</td>
                      <td>dal {formatData(b.validoDal)}{b.validoAl ? ` al ${formatData(b.validoAl)}` : ''}</td>
                      <td className="bop-text-muted">{formatDataOra(b.ultimoUtilizzoAt)}</td>
                      <td><span className={`bop-badge ${s.badge}`}>{s.label}</span></td>
                      <td className="bop-table__td--actions">
                        <BopRowActions actions={[
                          {
                            icon: <RowIcon.Disable />, label: 'Disattiva', variant: 'danger',
                            hidden: !b.attivo, onClick: () => disattiva(b),
                          },
                        ]} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {form && (
        <div className="bop-modal-overlay" onClick={() => setForm(null)}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">{form.id === null ? 'Nuovo badge' : 'Modifica badge'}</h2>
              <button className="bop-modal__close" onClick={() => setForm(null)} aria-label="Chiudi">✕</button>
            </div>
            <div className="bop-modal__body bop-ricarica__form">
              {errore && <div className="bop-alert bop-alert--danger bop-mb-0">{errore}</div>}
              <div className="bop-field">
                <label className="bop-field__label" htmlFor="badge-dip">
                  Dipendente <span className="bop-field__required">*</span>
                </label>
                <select id="badge-dip" className="bop-field__select" value={form.dipendenteId}
                  onChange={(e) => setForm({ ...form, dipendenteId: e.target.value })}>
                  <option value="">Scegli…</option>
                  {DIPENDENTI.map((d) => (<option key={d.id} value={d.id}>{d.cognome} {d.nome}</option>))}
                </select>
                {form.id !== null && (
                  <p className="bop-field__hint">
                    Cambiando dipendente, le ricariche già fatte restano intestate a chi aveva il badge in quel momento.
                  </p>
                )}
              </div>
              <div className="bop-field">
                <label className="bop-field__label" htmlFor="badge-codice">
                  Codice badge <span className="bop-field__required">*</span>
                </label>
                <input id="badge-codice" className="bop-field__input bop-text-mono" placeholder="Es. 04A1B2C3D4"
                  value={form.codice} disabled={form.id !== null}
                  onChange={(e) => setForm({ ...form, codice: e.target.value.toUpperCase() })} />
                <p className="bop-field__hint">
                  Il codice che la colonnina legge dal badge. Il modo più semplice per averlo: avvicinare il badge a una
                  colonnina e abilitarlo dai badge sconosciuti.
                </p>
              </div>
              <div className="bop-field">
                <label className="bop-field__label" htmlFor="badge-descr">Descrizione</label>
                <input id="badge-descr" className="bop-field__input" placeholder="Es. Badge presenze"
                  value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />
              </div>
              <div className="bop-form-row">
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="badge-dal">
                    Valido dal <span className="bop-field__required">*</span>
                  </label>
                  <input id="badge-dal" type="date" className="bop-field__input" value={form.validoDal}
                    onChange={(e) => setForm({ ...form, validoDal: e.target.value })} />
                </div>
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="badge-al">Valido fino al</label>
                  <input id="badge-al" type="date" className="bop-field__input" value={form.validoAl}
                    onChange={(e) => setForm({ ...form, validoAl: e.target.value })} />
                  <p className="bop-field__hint">Vuoto: senza scadenza</p>
                </div>
              </div>
              {form.id !== null && (
                <label className="bop-flex">
                  <input type="checkbox" checked={form.attivo}
                    onChange={(e) => setForm({ ...form, attivo: e.target.checked })} />
                  Badge attivo
                </label>
              )}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setForm(null)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salva}>
                {form.id === null ? 'Registra badge' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
